import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowRightLeft, CheckCircle, AlertTriangle, ShieldAlert } from 'lucide-react';

/**
 * IFT Status Tracker — Recordkeeping Bulletin 08-6
 * States:
 *  0 used → neutral (green)
 *  1 used → caution (amber)
 *  2 used → warning with A Fund restriction message
 */
export function IFTStatusCard({ used, limit, resetDate, loading }) {
  if (loading) {
    return (
      <div className="panel p-5" data-testid="ift-status-skeleton">
        <Skeleton className="h-3 w-32 mb-3 bg-gray-100" />
        <Skeleton className="h-6 w-24 mb-2 bg-gray-100" />
        <Skeleton className="h-3 w-48 bg-gray-100" />
      </div>
    );
  }

  const usedCount = used ?? 0;
  const limitCount = limit ?? 2;
  const atLimit = usedCount >= limitCount;
  const atCaution = usedCount === 1;

  // Format reset date
  const resetStr = resetDate
    ? new Date(resetDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '--';

  // State-driven colors
  const ringColor = atLimit ? '#DC2626' : atCaution ? '#D97706' : '#059669';
  const bgTint = atLimit ? 'bg-red-50 border-red-200' : atCaution ? 'bg-amber-50 border-amber-200' : 'bg-white border-[#E2E5E9]';
  const StatusIcon = atLimit ? ShieldAlert : atCaution ? AlertTriangle : CheckCircle;
  const statusIconColor = atLimit ? 'text-red-600' : atCaution ? 'text-amber-500' : 'text-emerald-600';
  const statusLabel = atLimit ? 'Limit Reached' : atCaution ? '1 Remaining' : 'Available';

  return (
    <div className={`panel p-5 ${bgTint}`} data-testid="ift-status-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="overline">Interfund Transfers</span>
        <ArrowRightLeft size={14} className="text-[#9CA3AF]" />
      </div>

      {/* Counter ring + number */}
      <div className="flex items-center gap-4">
        {/* Circular indicator */}
        <div className="relative w-14 h-14 shrink-0">
          <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
            {/* Background ring */}
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="#E2E5E9" strokeWidth="3" />
            {/* Progress ring */}
            <circle
              cx="18" cy="18" r="15.5" fill="none"
              stroke={ringColor} strokeWidth="3"
              strokeDasharray={`${(usedCount / limitCount) * 97.4} 97.4`}
              strokeLinecap="butt"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-mono text-base font-bold text-[#1B2A4A]" data-testid="ift-used-count">{usedCount}</span>
          </div>
        </div>

        <div>
          <div className="text-sm font-medium text-[#1B2A4A]" data-testid="ift-used-label">
            {usedCount} of {limitCount} IFTs used this month
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <StatusIcon size={13} className={statusIconColor} />
            <span className={`text-xs font-medium ${statusIconColor}`} data-testid="ift-status-label">{statusLabel}</span>
          </div>
        </div>
      </div>

      {/* Warning message when at limit */}
      {atLimit && (
        <div className="mt-3 bg-red-100 border border-red-300 rounded px-3 py-2.5" data-testid="ift-limit-warning">
          <p className="text-xs text-red-800 leading-relaxed">
            Monthly IFT limit reached. Only A Fund transfers are permitted until{' '}
            <span className="font-semibold">{resetStr}</span>.
            <span className="text-red-600 ml-1">(Recordkeeping Bulletin 08-6)</span>
          </p>
        </div>
      )}

      {/* Reset date */}
      <div className="mt-3 flex items-center justify-between text-[10px] text-[#9CA3AF]" data-testid="ift-reset-info">
        <span>Resets: {resetStr}</span>
        <span className="font-mono">5 C.F.R. § 1653</span>
      </div>
    </div>
  );
}
